import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { formatCartLineSummaryParts } from '../../pages/OurMenu/cartAddOnUtils';
import './AlreadyInCartSheet.css';

/**
 * Three-option sheet when an add-on item is already in the cart.
 * Portaled to document.body so card transforms/overflow cannot clip the overlay.
 * @param {{
 *   itemName?: string,
 *   lastLine: object,
 *   familyLines?: object[],
 *   onAddPlate: () => void,
 *   onEdit: () => void,
 *   onNew: () => void,
 *   onClose: () => void,
 * }} props
 */
export default function AlreadyInCartSheet({
  itemName,
  lastLine,
  familyLines = [],
  onAddPlate,
  onEdit,
  onNew,
  onClose,
}) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const summary = formatCartLineSummaryParts(lastLine);
  const otherCount = Math.max(0, (familyLines?.length || 0) - 1);
  const titleLabel = summary.titleWithPrice || '';

  const content = (
    <div className="aics-overlay" onClick={onClose} role="presentation">
      <div
        className="aics-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="aics-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="aics-close" onClick={onClose} aria-label="Close">
          <FaTimes />
        </button>

        <h2 id="aics-title" className="aics-title">
          Already in your cart
        </h2>
        <p className="aics-sub">
          Add another plate, choose new options, or edit what is already added. Add-ons stay at
          the qty in cart unless you increase them.
        </p>

        <div className="aics-summary">
          {titleLabel ? (
            <p className="aics-summary__customise">{titleLabel}</p>
          ) : null}
          {summary.hostLine ? (
            <p className="aics-summary__host">{summary.hostLine}</p>
          ) : null}
          {summary.addonLines.map((line) => (
            <p key={line} className="aics-summary__addon">
              {line}
            </p>
          ))}
          <span className="aics-summary__tag">Last added</span>
        </div>

        {otherCount > 0 ? (
          <p className="aics-multi">
            Also {otherCount} other customisation{otherCount === 1 ? '' : 's'} for this item in
            cart.
          </p>
        ) : null}

        <div className="aics-actions">
          <button type="button" className="aics-btn aics-btn--primary" onClick={onAddPlate}>
            Add another plate
          </button>
          <button type="button" className="aics-btn aics-btn--outline" onClick={onEdit}>
            Edit customisation
          </button>
          <button type="button" className="aics-btn aics-btn--outline" onClick={onNew}>
            New customisation
          </button>
          <button type="button" className="aics-btn aics-btn--ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(content, document.body);
}
